import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

/**
 * SeededListingsBatchNotice — Iter 134 (Feb 2026)
 *
 * One small, professional info notice rendered ONCE above any results grid
 * that contains seeded / unclaimed listings. Hidden automatically when the
 * current result set has no seeded entries, so users browsing fully-owned
 * categories never see it.
 *
 * Designed deliberately quiet:
 *   • Slate palette (not amber/red) so it never reads as a warning.
 *   • Single-line on desktop, gracefully wraps on mobile.
 *   • No CTAs — each individual seeded listing card already carries the
 *     "Unclaimed Listing" badge, and the business detail page surfaces
 *     the full claim / removal flow.
 *
 * Detection helper `hasSeededListing` is exported for callers that already
 * have a `businesses` array in hand — keeps the prop API tiny.
 */

export const hasSeededListing = (businesses) => {
  if (!Array.isArray(businesses) || businesses.length === 0) return false;
  return businesses.some((b) => {
    if (!b) return false;
    if (b.listingType === 'charity') return false;          // charities are never "seeded"
    // Iter 162 — Claimed / owner-verified / owner-managed listings are NOT seeded.
    if (b.ownershipStatus === 'owner_verified') return false;
    if (b.ownershipStatus === 'claimed') return false;
    if (b.isClaimed === true) return false;
    if (b.source === 'owner' || b.source === 'founders') return false;
    if (b.userId) return false;
    if (b.ownershipStatus === 'seeded' || b.ownershipStatus === 'unclaimed') return true;
    // Legacy records pre-iter-128 use `source === 'seed'` + `isClaimed`
    return (b.source === 'seed' || b.source === 'seeded') && !b.isClaimed;
  });
};

const SeededListingsBatchNotice = ({ businesses }) => {
  const { t } = useTranslation();
  const visible = useMemo(() => hasSeededListing(businesses), [businesses]);
  if (!visible) return null;

  return (
    <div
      className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 sm:px-4 sm:py-3
                 flex items-start gap-2 sm:gap-3"
      role="note"
      data-testid="seeded-listings-batch-notice"
    >
      <Info className="h-4 w-4 sm:h-[18px] sm:w-[18px] text-slate-500 flex-shrink-0 mt-[2px]" aria-hidden="true" />
      <p className="text-xs sm:text-sm leading-relaxed text-slate-700">
        {t(
          'seededListings.gridNotice',
          'Some listings are created using publicly available business information to help local users discover businesses in the area. Business owners can claim, edit or request removal at any time.',
        )}
      </p>
    </div>
  );
};

export default SeededListingsBatchNotice;
